import React, { useEffect, useState } from "react";
import { Link, withRouter } from "react-router-dom";

/**
 * Which heading the reader is in: the last one whose top has scrolled past the
 * sticky line. Recomputed on scroll, once per animation frame.
 */
function useActiveHeading(headings) {
  const [activeId, setActiveId] = useState(headings.length > 0 ? headings[0].id : null);

  useEffect(() => {
    if (headings.length === 0) return undefined;

    let frame = null;
    const update = () => {
      frame = null;
      // Roughly where the sticky panels start under the header (--sticky-top).
      const line = 120;
      const current = headings.reduce((found, heading) => {
        const element = document.getElementById(heading.id);
        return element && element.getBoundingClientRect().top <= line ? heading.id : found;
      }, headings[0].id);
      setActiveId(current);
    };
    const onScroll = () => {
      if (frame === null) frame = window.requestAnimationFrame(update);
    };

    update();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", onScroll);
      if (frame !== null) window.cancelAnimationFrame(frame);
    };
  }, [headings]);

  return activeId;
}

/**
 * The article's right column: contents, the help CTA, related articles.
 *
 * The contents panel only appears when the article has two or more headings.
 * None of the 76 articles in the October 2026 package uses markdown headings --
 * they use bold labels -- so for most articles the panel would otherwise be an
 * empty box.
 */
const ArticleAside = ({ headings, related, chatUrl, history, location }) => {
  const activeId = useActiveHeading(headings);

  // A hash change on the same route; routes/index.jsx scrolls to it. The href
  // is the full path because a bare "#id" resolves against <base href="/help/">
  // and would leave the article when opened in a new tab or copied.
  const target = id => ({ pathname: location.pathname, search: location.search, hash: `#${id}` });
  const goTo = id => e => {
    e.preventDefault();
    history.replace(target(id));
  };

  return (
    <aside className="hc-aside">
      {headings.length >= 2 ? (
        <div className="hc-panel">
          <p className="hc-panel__title">Di halaman ini</p>
          <nav className="hc-toc" aria-label="Isi artikel">
            {headings.map(heading => (
              <a
                key={heading.id}
                href={history.createHref(target(heading.id))}
                onClick={goTo(heading.id)}
                className={heading.id === activeId ? "is-current" : undefined}
                aria-current={heading.id === activeId ? "location" : undefined}
              >
                {heading.text}
              </a>
            ))}
          </nav>
        </div>
      ) : null}

      <div className="hc-panel hc-panel--cta">
        <p className="hc-panel__title">Masih bingung?</p>
        <p>Siapkan Order ID kamu, Team Kyou bisa cek langsung.</p>
        <a className="hc-btn hc-btn--block" href={chatUrl} target="_blank" rel="noopener noreferrer">
          Chat Team Kyou
        </a>
      </div>

      {related.length > 0 ? (
        <div className="hc-panel">
          <p className="hc-panel__title">Artikel terkait</p>
          <ul className="hc-related">
            {related.map(article => (
              <li key={article.id}>
                <Link to={`/${article.id}`}>{article.title}</Link>
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </aside>
  );
};

export default withRouter(ArticleAside);
